// ui.js — Sistema único de botones de /admin/users (#1416, R35, R36).
//
// Mockup elegido (sección COMÚN "Sistema único de botones",
// tools/mockups/smartdx_mockups_admin_users.html): un solo alto (32 px),
// forma pill, peso 600, el loader siempre como `startIcon`. Reemplaza los
// cuatro estilos de botón que convivían en la `UsersAdmin.js` monolítica.
//
// `AdminButton` envuelve `Button` de MUI: mismo `onClick`/`disabled`/
// `data-testid` que ya usan los tests existentes, solo cambia el estilo.
// Sebastián pidió explícito estandarizar TAMBIÉN las secciones movidas sin
// cambio de lógica (SuscripcionSection, UnitPromptsSection, AuditLogSection,
// DocumentsDialog — "estandarizar botones" es parte literal del pedido de
// #1416, y ahí vivían los 4 estilos que listó el diagnóstico): pasan por acá
// con la MISMA regla de las secciones nuevas (KpiStrip, ActivityFeed, las 3
// tabs, el drawer, el overlay) — solo la capa de presentación, cero cambio de
// handler/estado/payload/validación, mismo `data-testid` y mismo texto
// visible. `UsageTrafficPanel` queda afuera (oculto por `SHOW_VISUALIZER`).
import { Button, CircularProgress, IconButton, TextField, Tooltip } from "@mui/material";
import { PALETTE } from "./shared";

const VARIANT_SX = {
  primary: {
    bgcolor: PALETTE.accent,
    color: "var(--text-on-primary)",
    "&:hover": { bgcolor: PALETTE.accent, filter: "brightness(1.1)" },
    "&.Mui-disabled": { bgcolor: "rgba(var(--fg-rgb),0.08)", color: PALETTE.textFaint },
  },
  success: {
    bgcolor: PALETTE.success,
    color: "var(--text-on-success)",
    "&:hover": { bgcolor: PALETTE.success, filter: "brightness(1.08)" },
    "&.Mui-disabled": { bgcolor: "rgba(var(--fg-rgb),0.08)", color: PALETTE.textFaint },
  },
  danger: {
    bgcolor: PALETTE.danger,
    color: "var(--text-on-danger)",
    "&:hover": { bgcolor: PALETTE.danger, filter: "brightness(1.08)" },
    "&.Mui-disabled": { bgcolor: "rgba(var(--fg-rgb),0.08)", color: PALETTE.textFaint },
  },
  secondary: {
    bgcolor: "transparent",
    color: "var(--text-primary)",
    border: `1px solid ${PALETTE.borderMedium}`,
    "&:hover": { bgcolor: "rgba(var(--fg-rgb),0.05)" },
    "&.Mui-disabled": { color: PALETTE.textFaint, borderColor: PALETTE.borderSoft },
  },
  ghost: {
    bgcolor: "transparent",
    color: PALETTE.textMuted,
    "&:hover": { bgcolor: "rgba(var(--fg-rgb),0.05)" },
    "&.Mui-disabled": { color: PALETTE.textFaint },
  },
  // R15 (D12 del líder): "Revocar sesiones" es la única acción sensible del
  // drawer que no encaja en ninguna de las 5 variantes de arriba — antes
  // llevaba un `sx` de color propio (override), ahora es una variante más
  // del sistema, nunca un `sx` ad-hoc por card.
  warning: {
    bgcolor: "transparent",
    color: PALETTE.warning,
    border: `1px solid rgba(var(--color-warning-rgb),0.4)`,
    "&:hover": { bgcolor: "rgba(var(--color-warning-rgb),0.08)" },
    "&.Mui-disabled": { color: PALETTE.textFaint, borderColor: PALETTE.borderSoft },
  },
  // #1544: "Eliminar cuenta" de la Zona de peligro. Contorno y no relleno
  // (`danger`): el botón lleno queda para la confirmación final del diálogo,
  // así el que abre y el que borra no se ven iguales.
  dangerOutline: {
    bgcolor: "transparent",
    color: PALETTE.danger,
    border: `1px solid rgba(var(--color-danger-rgb),0.45)`,
    "&:hover": { bgcolor: "rgba(var(--color-danger-rgb),0.08)" },
    "&.Mui-disabled": { color: PALETTE.textFaint, borderColor: PALETTE.borderSoft },
  },
};

// `default` (32 px, el resto de las acciones de cabecera) | `small` (26 px,
// SOLO los presets de Acceso, R15 ampliado). `height:32` estaba fijo antes,
// sin mirar ningún prop.
const SIZE_SX = {
  default: { height: 32, fontSize: 13, px: 1.75 },
  small: { height: 26, fontSize: 12, px: 1.25 },
};

// `primary | success | danger | secondary | ghost | warning | dangerOutline` (design.md,
// "Firmas / contratos nuevos"). El loader reemplaza `startIcon` mientras
// `loading` es true — nunca un spinner aparte que empuje el texto.
// `data-variant` (D8 del líder) expone la variante en el DOM para que
// R15/R27/R34 se verifiquen sin depender de un prop de React no observable.
export const AdminButton = ({
  variant = "secondary",
  size = "default",
  loading = false,
  startIcon,
  disabled,
  sx,
  children,
  ...props
}) => (
  <Button
    disableRipple
    disableElevation
    data-variant={variant}
    startIcon={loading ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : startIcon}
    disabled={disabled || loading}
    sx={{
      borderRadius: 999,
      fontWeight: 600,
      textTransform: "none",
      boxShadow: "none",
      whiteSpace: "nowrap",
      ...SIZE_SX[size],
      ...VARIANT_SX[variant],
      ...sx,
    }}
    {...props}
  >
    {children}
  </Button>
);

// Reemplaza las pseudo-tabs de cobro (#862, "Cobrar el mes"/"Cobrar la
// implementación") y el selector de rango del panel de tráfico:
// `options = [{value, label, icon?}]`. `onChange(value)` es lo único que se
// llama — la lógica de qué hacer con cada valor (incluido cualquier no-op de
// "ya estaba en esa opción") vive en el caller, sin cambios.
export const SegmentedControl = ({ options, value, onChange, size = "medium", sx, ...props }) => (
  <span
    role="group"
    style={{
      display: "inline-flex",
      background: "var(--bg-input)",
      border: `1px solid ${PALETTE.borderSoft}`,
      borderRadius: 999,
      padding: 3,
      ...sx,
    }}
    {...props}
  >
    {options.map((opt) => {
      const on = opt.value === value;
      return (
        <span
          key={String(opt.value)}
          data-testid={opt.testId}
          role="button"
          aria-pressed={on}
          tabIndex={0}
          onClick={() => onChange(opt.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onChange(opt.value);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: size === "small" ? "4px 10px" : "5px 14px",
            borderRadius: 999,
            fontSize: size === "small" ? 12 : 13,
            fontWeight: 600,
            color: on ? "var(--text-on-primary)" : PALETTE.textMuted,
            background: on ? PALETTE.accent : "transparent",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          {opt.icon}
          {opt.label}
        </span>
      );
    })}
  </span>
);

// `IconButton` envuelto en `Tooltip`, CON `aria-label` obligatorio (R36): un
// ícono sin texto visible nunca es accesible solo por el `Tooltip` de MUI
// (que agrega `aria-describedby`, no `aria-label` — Playwright y los lectores
// de pantalla calculan el nombre accesible por `aria-label`/texto/`title`,
// nunca por `aria-describedby`).
//
// El `<span>` intermedio SOLO se agrega si `disabled` (es el workaround que
// pide MUI para que el Tooltip siga funcionando sobre un botón que no
// dispara eventos de mouse): con un botón habilitado, envolverlo igual hace
// que Tooltip clone ese `<span>` con su PROPIO `aria-label` (además del que
// ya trae el `IconButton`) — dos elementos con el mismo nombre accesible
// dentro del mismo árbol, un selector por rol/label deja de ser único.
export const IconAction = ({ label, icon, onClick, disabled, size = "small", sx, ...props }) => {
  const boton = (
    <IconButton
      size={size}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      sx={{ bgcolor: "transparent !important", color: PALETTE.textMuted, ...sx }}
      {...props}
    >
      {icon}
    </IconButton>
  );
  if (!disabled) return <Tooltip title={label}>{boton}</Tooltip>;
  return (
    <Tooltip title={label}>
      <span>{boton}</span>
    </Tooltip>
  );
};

// R17/R18: shell único para todo campo de texto, número o selector del
// drawer (Datos, Suscripción, Registrar un pago) — mismo alto, mismo borde,
// mismo tamaño de letra, MISMA pieza para un `<TextField>` normal, uno con
// `select` (reemplaza al `<Select>` suelto que usaba Datos) y uno con
// `type="date"` (D11: SIN máscara, el selector nativo del sistema operativo
// se queda, este wrapper solo le da el mismo shell visual — cero cambio de
// `value`/`onChange`/validación). `fullWidth` por defecto porque las grillas
// de 2/3 columnas ya fijan el ancho de la celda.
export const AdminTextField = ({ sx, ...props }) => (
  <TextField
    size="small"
    fullWidth
    sx={{
      "& .MuiInputBase-root": { height: 36, fontSize: 13, bgcolor: PALETTE.bgInput },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderSoft },
      "& .MuiInputLabel-root": { fontSize: 13 },
      ...sx,
    }}
    {...props}
  />
);
